import { useState, useEffect } from "react";
import {
  Clock,
  MapPin,
  Play,
  Square,
  Coffee,
  LogIn,
  LogOut,
  Loader2,
  CheckCircle,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Badge } from "../../../../components/ui/badge";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { useToast } from "../../../../hooks/use-toast";
import { cn } from "../../../../lib/utils";
import { attendanceAPI } from "../../../../lib/api";

export default function TimeClockWidget({
  attendanceStatus: propAttendanceStatus,
  onClockAction,
  currentTime: initialTime,
  todayAttendance: propTodayAttendance,
  onAttendanceUpdate, // Add the prop that's actually being passed
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialTime || new Date());
  const [attendanceStatus, setAttendanceStatus] = useState(
    propAttendanceStatus || null
  );
  const [todayAttendance, setTodayAttendance] = useState(
    propTodayAttendance || null
  );

  // Load attendance status if not provided as prop
  useEffect(() => {
    if (!propAttendanceStatus) {
      loadAttendanceStatus();
    } else {
      setAttendanceStatus(propAttendanceStatus);
    }
  }, [propAttendanceStatus]);

  // Refresh status when component becomes visible (for better UX)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !propAttendanceStatus) {
        loadAttendanceStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [propAttendanceStatus]);

  const loadAttendanceStatus = async () => {
    try {
      const response = await attendanceAPI.getCurrentStatus();
      if (response.data.success) {
        console.log("Attendance status loaded:", response.data.data);
        setAttendanceStatus(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error("Error loading attendance status:", error);
      throw error;
    }
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Get user location on component mount
  useEffect(() => {
    // Ne plus essayer automatiquement d'obtenir la géolocalisation
    // L'utilisateur peut l'activer manuellement s'il le souhaite
  }, []);

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      console.log(
        "Géolocalisation non supportée - pointage sera possible sans localisation"
      );
      return;
    }

    setLocationLoading(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationLoading(false);
      },
      (error) => {
        console.log(
          "Géolocalisation non disponible - pointage sera possible sans localisation:",
          error.message
        );
        setLocationLoading(false);
        // Pas de toast d'erreur - la géolocalisation est optionnelle
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  };

  const handleClockAction = async (action) => {
    setLoading(true);
    try {
      if (onClockAction) {
        // Pass location if available, otherwise pass null
        await onClockAction(action, location);
      } else {
        // Fallback implementation using direct API calls
        const requestData = location ? { location } : {}; // Only include location if it exists

        switch (action) {
          case "clock-in":
            await attendanceAPI.clockIn(requestData);
            break;
          case "clock-out":
            await attendanceAPI.clockOut(requestData);
            break;
          case "break-start":
            await attendanceAPI.recordBreak({ type: "pause_debut" });
            break;
          case "break-end":
            await attendanceAPI.recordBreak({ type: "pause_fin" });
            break;
          default:
            throw new Error("Action non reconnue");
        }

        // Reload attendance status immediately after successful action
        await loadAttendanceStatus();

        // Call the update callback if provided
        if (onAttendanceUpdate) {
          onAttendanceUpdate();
        }
      }

      toast({
        title: "Succès",
        description: "Pointage enregistré avec succès",
        variant: "default",
      });
    } catch (error) {
      console.error("Clock action error:", error);
      toast({
        title: "Erreur",
        description:
          error.response?.data?.error ||
          // error.response?.data?.message ||
          error.message ||
          "Erreur lors du pointage",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const canClockIn = !attendanceStatus?.clockInTime;

  const canClockOut =
    attendanceStatus?.clockInTime &&
    !attendanceStatus?.clockOutTime &&
    attendanceStatus?.status !== "left";
  const canBreak =
    attendanceStatus?.clockInTime &&
    !attendanceStatus?.clockOutTime &&
    attendanceStatus?.status !== "left";

  // Debug logging
  console.log("TimeClockWidget state:", {
    canClockIn,
    canClockOut,
    canBreak,
    clockInTime: attendanceStatus?.clockInTime,
    clockOutTime: attendanceStatus?.clockOutTime,
    status: attendanceStatus?.status,
    isPresent: attendanceStatus?.isPresent,
    onBreak: attendanceStatus?.onBreak,
    loading,
  });

  const getWorkingHours = () => {
    if (!attendanceStatus?.clockInTime) return "0h 0m";

    // Parse time strings in HH:MM:SS format to today's date
    const today = new Date();
    const todayDateString = today.toISOString().split("T")[0];

    let clockInTime;
    if (typeof attendanceStatus.clockInTime === "string") {
      // Handle HH:MM:SS format from backend
      if (attendanceStatus.clockInTime.includes(":")) {
        clockInTime = new Date(
          `${todayDateString}T${attendanceStatus.clockInTime}`
        );
      } else if (attendanceStatus.clockInTime.includes("T")) {
        clockInTime = new Date(attendanceStatus.clockInTime);
      }
    } else if (attendanceStatus.clockInTime instanceof Date) {
      clockInTime = attendanceStatus.clockInTime;
    }

    let endTime;
    if (attendanceStatus.clockOutTime) {
      if (typeof attendanceStatus.clockOutTime === "string") {
        // Handle HH:MM:SS format from backend
        if (attendanceStatus.clockOutTime.includes(":")) {
          endTime = new Date(
            `${todayDateString}T${attendanceStatus.clockOutTime}`
          );
        } else if (attendanceStatus.clockOutTime.includes("T")) {
          endTime = new Date(attendanceStatus.clockOutTime);
        }
      } else if (attendanceStatus.clockOutTime instanceof Date) {
        endTime = attendanceStatus.clockOutTime;
      }
    } else {
      endTime = currentTime;
    }

    if (!clockInTime) return "0h 0m";

    const diffMs = endTime - clockInTime;
    const diffHours = diffMs / (1000 * 60 * 60);
    const hours = Math.floor(diffHours);
    const minutes = Math.floor((diffHours % 1) * 60);

    return `${hours}h ${minutes}m`;
  };

  const formatTime = (timeValue) => {
    if (!timeValue) return "--:--";

    try {
      let date;
      if (timeValue instanceof Date) {
        date = timeValue;
      } else if (typeof timeValue === "string") {
        // Handle HH:MM:SS format from backend
        if (timeValue.includes(":") && !timeValue.includes("T")) {
          const today = new Date().toISOString().split("T")[0];
          date = new Date(`${today}T${timeValue}`);
        } else if (timeValue.includes("T")) {
          date = new Date(timeValue);
        } else {
          date = new Date(`2000-01-01T${timeValue}`);
        }
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid date value:", timeValue);
        return "--:--";
      }

      return date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting time:", error, timeValue);
      return "--:--";
    }
  };

  return (
    <Card className="h-fit">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Pointage
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Time */}
        <div className="text-center py-4">
          <p className="text-3xl font-mono font-bold">
            {currentTime.toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {currentTime.toLocaleDateString("fr-FR", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </p>
        </div>

        {/* Today's Status */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Arrivée:</span>
            <span className="font-medium">
              {formatTime(attendanceStatus?.clockInTime)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Départ:</span>
            <span className="font-medium">
              {formatTime(attendanceStatus?.clockOutTime)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Statut:</span>
            <Badge
              variant={attendanceStatus?.isPresent ? "default" : "secondary"}
              className={cn(
                attendanceStatus?.isPresent && "bg-green-100 text-green-700"
              )}
            >
              {attendanceStatus?.isPresent ? "Présent" : "Absent"}
            </Badge>
          </div>
          {attendanceStatus?.onBreak && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">En pause:</span>
              <Badge variant="outline">
                <Coffee className="h-3 w-3 mr-1" />
                Pause
              </Badge>
            </div>
          )}
        </div>

        {/* Location Status - Optionnel */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-700">
              Géolocalisation (optionnel):
            </span>
          </div>
          <div className="flex items-center gap-2">
            {locationLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            ) : location ? (
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-xs text-green-600">Activée</span>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={getCurrentLocation}
                className="text-xs text-blue-600 hover:text-blue-700 h-6 px-2"
              >
                Activer
              </Button>
            )}
          </div>
        </div>

        {/* Clock Buttons */}
        <div className="space-y-3">
          {/* Clock In */}
          <Button
            className="w-full"
            disabled={!canClockIn || loading}
            onClick={() => handleClockAction("clock-in")}
            variant={canClockIn ? "default" : "outline"}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <LogIn className="h-4 w-4 mr-2" />
            )}
            Pointer l'arrivée
          </Button>

          {/* Break Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canBreak || attendanceStatus?.onBreak || loading}
              onClick={() => handleClockAction("break-start")}
            >
              <Coffee className="h-3 w-3 mr-1" />
              Pause
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canBreak || !attendanceStatus?.onBreak || loading}
              onClick={() => handleClockAction("break-end")}
            >
              <Play className="h-3 w-3 mr-1" />
              Reprendre
            </Button>
          </div>

          {/* Clock Out */}
          <Button
            variant="destructive"
            className="w-full"
            disabled={!canClockOut || loading}
            onClick={() => handleClockAction("clock-out")}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4 mr-2" />
            )}
            Pointer le départ
          </Button>
        </div>

        {/* Working Time Today */}
        {/* {attendanceStatus?.clockInTime && (
          <div className="pt-4 border-t text-center">
            <p className="text-sm text-muted-foreground mb-1">
              Temps travaillé aujourd'hui
            </p>
            <p className="text-lg font-semibold text-blue-600">
              {getWorkingHours()}
            </p>
          </div>
        )} */}

        {/* Location Info */}
        {!location && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-xs text-green-700">
              Vous pouvez pointer sans géolocalisation. Activez-la uniquement si
              nécessaire.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
